import { useEffect, useRef, useState } from 'react';
import arrowDownIcon from '../../../assets/icons/chevron-down-white.svg';
import arrowRightIcon from '../../../assets/icons/chevron-right-default-gray-900.svg';
import DataUrlHelper from '../../helpers/data-url.helper';
import LocationAutocomplete, { type ILocationAutocompleteState, type ILocationAutocompleteSelection } from '../Shared/LocationAutocomplete';
import { LocationAutocompleteSelectionType } from '../Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';
import { LocationType } from '../../models/location-type.enum';
import ValidationInfo from '../../validation/validation-info';
import DepartureBoard from './components/DepartureBoard';
import { GeolocationService, type Coordinates } from '../../services/geolocation.service';
import type { ILocation } from '../../models/location';
import { ApiPlaneraResaService } from '../../services/api-planera-resa/api-planera-resa.service';
import './style.scss';
import { AutocompleteLabelPosition } from '../Shared/AutocompleteBase/autocomplete-base.enum';
import { AnalyticsService } from '../../services/analytics-service';
import { IServerSideRendered } from '../Shared/Base/server-side-rendered';
import { t } from '../../services/texts.service';

function getLocationsFromCoordinates(coordinates: Coordinates): Promise<ILocation[]> {
  return ApiPlaneraResaService.getLocationsByCoordinates(coordinates.latitude, coordinates.longitude, 500, [LocationType.stopArea], 1);
}

export interface INextDepartureSettings {
  showLabsPromotion?: boolean;
  labsPromotionHref?: string;
}

export interface INextDepartureTexts {
  labsPromotionText?: string;
}

type INextDepartureProps = IServerSideRendered<INextDepartureTexts, INextDepartureSettings>;

function NextDeparture({ settings, texts }: INextDepartureProps) {
  const [show, setShow] = useState(false);
  const [currentStopArea, setCurrentStopArea] = useState<ILocation | undefined>(undefined);
  const [locationAutocompleteState, setLocationAutocompleteState] = useState<ILocationAutocompleteState>({
    value: undefined,
    validationInfo: new ValidationInfo(),
  });
  const showLabsPromotion = show && settings.showLabsPromotion && settings.labsPromotionHref;
  const currentPositionAbortController = useRef(new AbortController());

  useEffect(() => {
    async function setStopAreaFromCurrentPosition() {
      try {
        const coordinates = await GeolocationService.getCurrentPosition(currentPositionAbortController.current.signal);
        const locations = await getLocationsFromCoordinates(coordinates);
        if (locations.length > 0) {
          setCurrentStopArea(locations[0]);
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          // Intentionally cancelled
          return;
        }
        setCurrentStopArea(undefined);
      }
    }

    if (show && !currentStopArea) {
      setStopAreaFromCurrentPosition();
    } else if (currentStopArea) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocationAutocompleteState({ value: { type: LocationAutocompleteSelectionType.location, location: currentStopArea }, validationInfo: new ValidationInfo() });
    }
  }, [show, currentStopArea]);

  useEffect(() => {
    if (show) {
      document.body.classList.add('has-overlay-sm');
    } else {
      document.body.classList.remove('has-overlay-sm');
    }
  }, [show]);

  function handleOnLocationSelect(value: ILocationAutocompleteSelection | undefined) {
    if (value?.location) {
      setCurrentStopArea(value.location);
      AnalyticsService.addEvent(AnalyticsService.NextDepartureCategory, 'Val av hållplats', value.location?.name ?? '');
    }
    setLocationAutocompleteState({
      value: undefined,
      validationInfo: new ValidationInfo(),
    });
  }

  function handleOnShowChange() {
    const newShow = !show;
    setShow(newShow);
    AnalyticsService.addEvent(AnalyticsService.NextDepartureCategory, 'Klick på fliken', newShow ? 'Öppna' : 'Stäng');
  }

  function handleOnLabsPromotionClick() {
    if (!settings.labsPromotionHref) {
      return;
    }

    AnalyticsService.addEvent(AnalyticsService.NextDepartureCategory, 'Klick på Labs-puff', settings.labsPromotionHref);
  }

  return (
    <div className={`next-departure position-relative d-md-none ${show ? '' : 'd-print-none'}`}>
      <div id="next-departure-content" className={`next-departure__content d-flex flex-column outline-light w-100 ${show ? 'next-departure__content--show' : ''}`}>
        {show && (
          <div className="w-100 pt-2 pb-3 d-flex next-departure__search next-departure__search position-relative justify-content-center">
            <div className="container">
              <label className="visually-hidden" htmlFor="next-departure-stop">
                {t('Hållplats', 'Stop')}
              </label>
              <LocationAutocomplete
                id="next-departure-stop"
                labelPosition={AutocompleteLabelPosition.hide}
                locationTypes={[LocationType.stopArea]}
                state={locationAutocompleteState}
                showValidationMessages={false}
                placeholderText={t('Hållplats', 'Stop')}
                onSelect={handleOnLocationSelect}
                onKeyUp={() => {
                  if (!currentPositionAbortController.current.signal.aborted) {
                    currentPositionAbortController.current.abort();
                  }
                }}
              />
            </div>
          </div>
        )}
        {show && currentStopArea && (
          <div className="next-departure__board flex-grow-1">
            <DepartureBoard stopArea={currentStopArea} />
          </div>
        )}
        {showLabsPromotion && (
          <a
            href={settings.labsPromotionHref}
            className="btn--unstyled next-departure__labs-promo w-100 bg-blue-50 text-color-gray-900 text-start p-2 border-bottom"
            onClick={handleOnLabsPromotionClick}
          >
            <span className="d-flex align-items-center justify-content-between gap-3">
              <span>
                <span className="next-departure__labs-promo-label d-inline-block border rounded-2 fw-bold px-2 py-1 mb-1">{t('Vi testar', 'Labs')}</span>
                <span className="d-block lh-sm">{texts.labsPromotionText}</span>
              </span>
              <img className="flex-shrink-0" src={DataUrlHelper.encode(arrowRightIcon)} alt="" width="20" height="20" />
            </span>
          </a>
        )}
        <button
          onClick={handleOnShowChange}
          className="btn--unstyled w-100 next-departure__button position-absolute btn--outline-internal d-flex align-items-center justify-content-center gap-2"
          type="button"
          aria-expanded={show ? 'true' : 'false'}
          aria-controls="next-departure-content"
        >
          <span className="fw-bold">{t('Visa nästa tur', 'Next departure')}</span>
          <img className="flex-shrink-0" src={DataUrlHelper.encode(arrowDownIcon)} alt="" />
        </button>
      </div>
    </div>
  );
}

export default NextDeparture;
